/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { FunctionComponent, useEffect, useMemo, useState } from 'react';

import SelectMark from '../../../../assets/images/hydra_icon_status_checkmark.png';
import { Locale, LocaleKeys } from '../../../../core/locale';
import { CursorManager, KeyManager } from '../../../../core/navigation';
import {
    RootState,
    useTivoDispatch,
    useTivoSelector,
} from '../../../../core/tivo/tivo-redux-wrapper';
import { ListTypes } from '../../../../core/utils';
import { setListEntryFocus } from '../../../../store/list/list.actions';
import { setAutoplayNextEpisodeOptionsList } from '../../../../store/settings/userpreferences/userprefautoplaynextepisode/userprefautoplaynextepisode.reducers';
import { List, Mousetrap, ScreenPreview } from '../../../common';
import { SettingsTemplate } from '../../SettingsTemplate';

import '../../SettingsCommon.scss';

const AutoplayNextEpisode: FunctionComponent = (): JSX.Element => {
    const dispatch = useTivoDispatch();
    const [isCursorActive, setIsCursorActive] = useState(false);
    const { optionsList, selectedOption } = useTivoSelector(
        (state: RootState) => state.userPrefAutoplayNextEpisode,
    );

    const selectedOptionIndex = useMemo(() => {
        return optionsList.reduce(
            (acc, option, index) => (option.value === selectedOption?.value ? index : acc),
            0,
        );
    }, [optionsList, selectedOption]);

    const onCursorStateChangeCallback = (state) => {
        setIsCursorActive(state);
        if (!isCursorActive) dispatch(setListEntryFocus(ListTypes.AUTOPLAY_NEXT_EPISODE, true));
    };

    useEffect(() => {
        dispatch(setAutoplayNextEpisodeOptionsList());
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);

        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }
        return () => {
            CursorManager.unsubscribe(refCursorManager);
        };
    }, []);

    const renderAutoplayNextEpisodeList = () => {
        return (
            <div className="settings-common-content list-with-ticks">
                <div className="description-text">
                    {Locale.getString(LocaleKeys.SETTINGS_AUTOPLAY_NEXT_EPISODE_DESCRIPTION)}
                </div>
                <List
                    type={ListTypes.AUTOPLAY_NEXT_EPISODE}
                    itemList={optionsList}
                    initialFocusedIndex={selectedOptionIndex}
                    initialSelectedIndex={selectedOptionIndex}
                    generateItemView={(item: any, itemClass, index) => (
                        <Mousetrap
                            key={index}
                            value={`${index}_${ListTypes.AUTOPLAY_NEXT_EPISODE}`}
                            className={`item ${itemClass}`}
                        >
                            {item.isSelected && (
                                <span className="select-mark-span">
                                    <img src={SelectMark} className="select-mark" />
                                </span>
                            )}
                            <span className={`${!item.isSelected && 'list-value-without-tick'}`}>
                                {item.value}
                            </span>
                        </Mousetrap>
                    )}
                />
            </div>
        );
    };

    return (
        <SettingsTemplate title={Locale.getString(LocaleKeys.SETTINGS_AUTOPLAY_NEXT_EPISODE)}>
            {renderAutoplayNextEpisodeList()}
            <ScreenPreview>
                <div>{selectedOption?.preview}</div>
            </ScreenPreview>
        </SettingsTemplate>
    );
};

export default AutoplayNextEpisode;
